import {
    CLICKED_MEDICARE_BANNER_CHOICE_KEY,
    type DrugPathwayOptions,
    type PathwayAlertConfig,
    type PathwayOptionConfig,
    type PathwayQuestionnaireConfig,
    type ProgramTerms,
} from '../../shared';
import {
    REVIEW_DETAILS_DESCRIPTION,
    STANDARD_INSURANCE_QUESTIONNAIRE_OPTIONS,
    ZEPBOUND_MEDICARE_BRIDGE_CARD_BASE,
    ZEPBOUND_SELF_PAY_CARD_BASE,
    ZEPBOUND_TERMS_URL,
} from '../shared';

export const zepboundAutoinjectorPathwayAlerts: PathwayAlertConfig[] = [
    {
        pathways: ['fallback'],
        screens: ['coverage-select', 'coverage-sub-select'],
        color: 'info',
        content:
            '<b>Eligible Medicare Part D patients may have coverage through the Medicare GLP\u20111 Bridge.</b>',
        cta: {
            text: 'I have Medicare Part D',
            patientChoiceKey: CLICKED_MEDICARE_BANNER_CHOICE_KEY,
            target: {
                pathway: 'government',
                subPathway: 'medicare-bridge',
            },
        },
    },
    {
        pathways: ['government'],
        screens: ['path-select'],
        subPathways: ['medicare-bridge'],
        color: 'info',
        content:
            "If you have been diagnosed with moderate-to-severe obstructive sleep apnea (OSA), you are not eligible for Medicare GLP\u20111 Bridge and you should choose the second option below. <b>If you're not sure which option is right for you, speak with your healthcare provider.</b>",
    },
];

export const zepboundAutoinjectorTerms: ProgramTerms = {
    'self-pay': {
        heading: 'LillyDirect® Zepbound® AutoInjector',
        body: '',
        termsUrl: ZEPBOUND_TERMS_URL,
        incentiveProgram: {
            name: 'Zepbound® AutoInjector',
            disclaimer:
                'You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® KwikPen® or the Zepbound® Vial for an approved use consistent with FDA-approved product labeling.',
            terms: '',
        },
    },
};

export const zepboundAutoinjectorQuestionnaire: PathwayQuestionnaireConfig = {
    heading: 'What type of insurance do you have?',
    description:
        'This helps us match you with the most relevant pharmacy and savings options.',
    notFoundText: 'Not sure what type of insurance you have?',
    notFoundModal: {
        heading: 'Not sure what type of insurance you have?',
        body: "Check your insurance card — it usually shows your plan name and type. If you're still not sure, contact your insurance company directly using the number on the back of your card; they can confirm your plan type in just a few minutes.<br><br>You can also visit the <a>Zepbound®</a> website to learn more about your insurance and how your medication may be covered.<br><br>Once you have the information you need, you can return to this form and select the insurance option that applies to you.",
        linkUrl: 'https://zepbound.lilly.com/insurance-check',
    },
    options: [
        ...STANDARD_INSURANCE_QUESTIONNAIRE_OPTIONS,
        {
            id: 'has-insurance-self-pay',
            content: {
                heading: 'I have insurance but want to self-pay',
                description:
                    "My plan doesn't cover this medication, or I prefer not to use my insurance",
            },
            target: { pathway: 'self-pay', destination: 'path-select' },
        },
        {
            id: 'no-insurance',
            content: {
                heading: "I don't have insurance",
                description: "I'll pay without using insurance",
            },
            target: { pathway: 'self-pay', destination: 'path-select' },
        },
    ],
};

// Medicare GLP\u20111 Bridge — requires switching to KwikPen.
const medicareBridgeCard: PathwayOptionConfig = {
    ...ZEPBOUND_MEDICARE_BRIDGE_CARD_BASE,
    subPathway: 'medicare-bridge',
    content: {
        ...ZEPBOUND_MEDICARE_BRIDGE_CARD_BASE.content,
        fallbackTermsUrl: ZEPBOUND_TERMS_URL,
        fallbackDisclaimer:
            'Additional taxes and fees may apply. You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® KwikPen® or the Zepbound® Vial for an approved use consistent with FDA-approved product labeling.',
    },
};

// Medicare Part D direct coverage — patient stays on AutoInjector, no switch required.
const medicarePartDCard: PathwayOptionConfig = {
    id: 'medicare-part-d',
    fillPathway: 'government',
    subPathway: 'medicare-part-d',
    destination: 'pharmacy-select',
    headlinePriceType: 'MedicarePartD',
    content: {
        heading: 'Medicare Part D',
        headerIcon: 'security',
        noPriceText: 'Pricing available at pharmacy',
        bullets: [
            {
                key: 'noChange',
                icon: 'check',
                color: 'success',
                text: 'No prescription change needed — you can proceed with your Zepbound® AutoInjector.',
            },
            {
                key: 'pharmacyCoverage',
                icon: 'check',
                color: 'success',
                text: 'Your pharmacy will check your coverage for eligibility.',
            },
            {
                key: 'priorAuth',
                icon: 'warning',
                color: 'warning',
                text: '<b>May require prior authorization</b><br />Most plans require insurance approval. Work with your healthcare provider to ensure a prior authorization is submitted.',
            },
        ],
        button: 'Use Medicare Part D',
    },
};

const commercialInsuranceCard: PathwayOptionConfig = {
    id: 'commercial',
    fillPathway: 'insurance',
    headlinePriceType: 'CopayAssistance',
    content: {
        heading: 'Insurance',
        headerIcon: 'security',
        subHeading: 'with commercial insurance and savings card',
        showPrice: true,
        priceLabel: 'Starting at',
        priceSuperscript: '*',
        disclaimer:
            'For eligible and commercially insured patients with coverage for Zepbound, governmental beneficiaries excluded, terms and conditions apply. Check your insurance coverage by calling the number on the back of your benefit card.',
        fallbackPriceLabel: '<b>If covered</b>, otherwise starting at',
        fallbackSuperscript: '*',
        bullets: [
            {
                key: 'pharmacyCoverage',
                icon: 'check',
                color: 'success',
                text: 'Your pharmacy will check your coverage for eligibility.',
            },
            {
                key: 'priorAuth',
                icon: 'warning',
                color: 'warning',
                text: '<b>May require prior authorization</b><br />Most plans require insurance approval - may add 5-10 business days before shipment.',
            },
        ],
        button: 'Use insurance',
    },
};

// Shared government insurance card — also used by KwikPen.
const governmentInsuranceCard: PathwayOptionConfig = {
    id: 'other-government',
    fillPathway: 'government',
    subPathway: 'other-government',
    destination: 'pharmacy-select',
    headlinePriceType: 'GovernmentPriceAtPharmacy',
    content: {
        heading: 'Government Insurance',
        headerIcon: 'security',
        noPriceText: 'Cost confirmed by your selected pharmacy',
        bullets: [
            {
                key: 'pharmacyCoverage',
                icon: 'check',
                color: 'success',
                text: 'Coverage checked by your pharmacy — pricing confirmed based on your specific plan.',
            },
            {
                key: 'priorAuth',
                icon: 'warning',
                color: 'warning',
                text: '<b>May require prior authorization</b><br />Most plans require insurance approval which may take time.',
            },
        ],
        button: 'Use Government Insurance',
    },
};

// Auto-injector self-pay requires switching to KwikPen or Vial.
const selfPaySwitchCard: PathwayOptionConfig = {
    ...ZEPBOUND_SELF_PAY_CARD_BASE,
    content: {
        ...ZEPBOUND_SELF_PAY_CARD_BASE.content,
        disclaimer:
            'Additional taxes and fees may apply. You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® KwikPen® or the Zepbound® Vial for an approved use consistent with FDA-approved product labeling.',
    },
};

const retailPriceCard: PathwayOptionConfig = {
    id: 'retail-price',
    fillPathway: 'insurance',
    subPathway: 'retail',
    destination: 'pharmacy-select',
    headlinePriceType: 'Retail',
    content: {
        heading: 'Retail Price',
        headerIcon: 'local-pharmacy',
        subHeading: 'Standard pharmacy retail pricing',
        noPriceText: 'Pricing available at pharmacy',
        bullets: [
            {
                key: 'noChange',
                icon: 'check',
                color: 'success',
                text: 'No prescription change needed — proceed with your Zepbound® Single-Dose Pen.',
            },
            {
                key: 'higherPrice',
                icon: 'warning',
                color: 'warning',
                text: 'Without insurance, pricing may be higher than expected.',
            },
        ],
        button: 'Continue without insurance',
    },
};

export const zepboundAutoinjectorPathwayOptions: DrugPathwayOptions = {
    showEscapeHatch: true,
    default: { cards: [selfPaySwitchCard] },
    selfPayFavored: {
        heading:
            'Your current prescription is for the Zepbound® Single-Dose Pen.',
        description: 'Review your options below:',
        cards: [selfPaySwitchCard, retailPriceCard],
    },
    insuranceFavored: {
        heading: 'Based on your insurance selections, you may have options.',
        description: REVIEW_DETAILS_DESCRIPTION,
        cards: [commercialInsuranceCard, selfPaySwitchCard],
    },
    medicareBridge: {
        heading: 'Based on your insurance selections, you may have options.',
        description: REVIEW_DETAILS_DESCRIPTION,
        cards: [medicareBridgeCard, medicarePartDCard],
    },
    governmentInsurance: {
        heading: 'Based on your insurance selections, you may have options.',
        description:
            'Certain plans including TRICARE, Veterans Affairs, Indian Health Services, and Bureau of Prisons may cover Zepbound® AutoInjector.<br><br>Coverage varies for other types of government insurance. You can check your coverage <a>here</a>, or use the self-pay option below.',
        descriptionLinkUrl: 'https://zepbound.lilly.com/insurance-check',
        cards: [governmentInsuranceCard, selfPaySwitchCard],
    },
};
